import {
  type InputHTMLAttributes,
  use,
  useEffect,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';

import { useExternalUrlValidation } from '../../hooks/useExternalUrlValidation';
import { Input as InputBase } from '../Input';
import { UrlValidationStatus } from '../UrlValidationStatus';
import { FieldLabel } from './FieldLabel';
import { FormContext } from './FormContext';

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  checkReachability?: boolean;
  explanation?: string;
  label: string;
  name: string;
  reachabilityMode?: 'always' | 'changed';
  validateHttps?: boolean;
}

function getUrlFormatError(value: string, validateHttps: boolean) {
  if (!value.trim()) {
    return '';
  }

  try {
    const url = new URL(value);
    return validateHttps && url.protocol !== 'https:'
      ? 'Use an HTTPS link.'
      : '';
  } catch {
    return 'Enter a full URL, like https://example.com.';
  }
}

export function shouldRunUrlReachabilityCheck(input: {
  checkReachability: boolean;
  formatError: string;
  initialValue: string;
  reachabilityMode: 'always' | 'changed';
  value: string;
}) {
  return (
    input.checkReachability &&
    !input.formatError &&
    (input.reachabilityMode === 'always' ||
      input.value.trim() !== input.initialValue.trim())
  );
}

export function UrlInput(props: Props) {
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { setValid } = use(FormContext);
  const initialValueRef = useRef(props.defaultValue || props.value || '');
  const [value, setValue] = useState(initialValueRef.current);
  const {
    checkReachability = true,
    explanation,
    label,
    className,
    reachabilityMode = 'always',
    validateHttps = true,
    onChange,
    required,
    ...rest
  } = props;
  const valueString = typeof value === 'string' ? value : String(value);
  const initialValueString =
    typeof initialValueRef.current === 'string'
      ? initialValueRef.current
      : String(initialValueRef.current);
  const formatError = getUrlFormatError(valueString, validateHttps);
  const reachabilityShouldRun = shouldRunUrlReachabilityCheck({
    checkReachability,
    formatError,
    initialValue: initialValueString,
    reachabilityMode,
    value: valueString,
  });
  const localError = formatError || error;
  const reachability = useExternalUrlValidation(valueString, {
    enabled: reachabilityShouldRun,
  });
  const reachabilityError = reachability.blocking ? reachability.message : '';
  const customError = formatError || reachabilityError;
  const validationMessage =
    localError ||
    reachabilityError ||
    (reachability.status === 'idle' ? '' : reachability.message);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    // Native constraint messages must not become persistent custom errors.
    input.setCustomValidity(customError);
    setError(input.validationMessage);
    setValid(input.form?.checkValidity() ?? true);
  }, [setValid, customError, valueString]);

  const applyUrlInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    onChange?.(e);
  };

  return (
    <label className={twMerge('block', 'group', className)}>
      <FieldLabel label={label} required={required} explanation={explanation} />
      <InputBase
        ref={inputRef}
        type="url"
        wrapperClassName="mt-1"
        required={required}
        onChange={applyUrlInput}
        onInvalid={(e) => {
          setError(e.currentTarget.validationMessage);
        }}
        {...rest}
      />
      <UrlValidationStatus
        className="mt-1 justify-end"
        hasLocalError={Boolean(localError)}
        message={validationMessage}
        status={reachability.status}
      />
    </label>
  );
}
